import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";
import { currentGroupId } from "../utils/access";

const TYPE = "cms_get_live_charging_status";

// Live charger status for the logged-in user's stations (the server limits the
// stations by group). Polls every `intervalMs` while the tab is visible.
export default function useLiveChargingStatus({ intervalMs = 5000, paused = false } = {}) {
  const { send, isConnected, registerHandler } = useWebSocketContext();
  const [stations, setStations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [unsupported, setUnsupported] = useState(false);
  const [meterValues, setMeterValues] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const inFlight = useRef(false);
  const mountedRef = useRef(true);

  const fetchStatus = useCallback(async () => {
    if (!isConnected || inFlight.current || unsupported) return;
    inFlight.current = true;
    // an older backend answers unknown types with a generic "error" message
    const off = registerHandler("error", (msg) => {
      if (typeof msg.message === "string" && msg.message.includes(TYPE) && mountedRef.current) {
        setUnsupported(true);
        setIsLoading(false);
      }
    });
    try {
      const response = await send(TYPE, { group_id: currentGroupId() });
      if (!mountedRef.current) return;
      if (!response?.success || !Array.isArray(response.data)) {
        throw new Error(response?.message || "Couldn't load live status");
      }
      setStations(response.data);
      setMeterValues(response.meter_values !== false);
      setUpdatedAt(new Date());
      setError("");
    } catch (err) {
      if (mountedRef.current) setError(err.message);
    } finally {
      off();
      inFlight.current = false;
      if (mountedRef.current) setIsLoading(false);
    }
  }, [isConnected, send, registerHandler, unsupported]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    fetchStatus();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchStatus();
    }, intervalMs);
    const onVisible = () => document.visibilityState === "visible" && fetchStatus();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchStatus, intervalMs, paused]);

  return { stations, isLoading, error, unsupported, meterValues, updatedAt, refetch: fetchStatus };
}
